/**
 * LiveKit Loader
 *
 * Lazy loader for livekit-client (~130 KB gzipped) so voice and dictation
 * code does not ship with the initial chat bundle.
 *
 * Primary responsibilities:
 * - Import livekit-client once, on first voice/dictation start
 * - Expose the loaded module synchronously to callbacks that only run
 *   after a room exists
 *
 * Dependencies:
 * - livekit-client (dynamic import)
 */

export type LiveKitModule = typeof import('livekit-client')

let loaded: LiveKitModule | null = null
let loader: Promise<LiveKitModule> | null = null

// --------------

/** Load livekit-client once; safe to call repeatedly. */
export function loadLiveKit(): Promise<LiveKitModule> {
  if (loaded) return Promise.resolve(loaded)
  if (!loader) {
    loader = import('livekit-client')
      .then((module) => {
        loaded = module
        return module
      })
      .catch((error) => {
        // Allow a retry on the next start if the chunk failed to load
        loader = null
        throw error
      })
  }
  return loader
}

// --------------

/** Loaded module for callbacks that only run after loadLiveKit() resolved. */
export function getLoadedLiveKit(): LiveKitModule {
  if (!loaded) throw new Error('livekit-client not loaded; await loadLiveKit() first')
  return loaded
}
